import React, { useEffect, useMemo, useState } from "react";
import TabInfo from "./components/TabInfo";
import TabStudents from "./components/TabStudents";
import TabAttendance from "./components/TabAttendance";
import TabAssignments from "./TabAssignments/TabAssignments";
import TabReports from "./components/TabReports";
import TabNotifications from "./components/TabNotifications";
import TabActivities from "./components/TabActivities";

import {
  addStudentToClass,
  removeStudentFromClass,
  updateStudentStatus,
} from "./api/enrollments";
import { pickDisplayFromUser } from "../../firebase/roster";
import { logClassActivity } from "./api/activityLogger";
import {
  FiBell,
  FiBookOpen,
  FiCalendar,
  FiClipboard,
  FiFileText,
  FiMapPin,
  FiPieChart,
  FiUsers,
  FiActivity,
} from "react-icons/fi";

const tabList = [
  { key: "info", label: "Thông tin", icon: FiBookOpen },
  { key: "students", label: "Học viên", icon: FiUsers },
  { key: "attendance", label: "Điểm danh", icon: FiClipboard },
  { key: "assignments", label: "Bài tập/kiểm tra", icon: FiCalendar },
  { key: "reports", label: "Báo cáo", icon: FiPieChart },
  { key: "notifications", label: "Thông báo", icon: FiBell },
  { key: "activities", label: "Hoạt động", icon: FiActivity },
];

const weekdays = [
  { value: "mon", label: "Thứ 2" },
  { value: "tue", label: "Thứ 3" },
  { value: "wed", label: "Thứ 4" },
  { value: "thu", label: "Thứ 5" },
  { value: "fri", label: "Thứ 6" },
  { value: "sat", label: "Thứ 7" },
  { value: "sun", label: "CN" },
];

function displayText(value, fallback = "—") {
  if (!value) return fallback;
  if (typeof value === "string") return value;
  if (typeof value === "object") return value.vi || value.en || fallback;
  return String(value);
}

function renderScheduleShort(schedule = []) {
  if (!Array.isArray(schedule) || schedule.length === 0) return "Chưa có lịch";
  const labelMap = weekdays.reduce((acc, item) => ({ ...acc, [item.value]: item.label }), {});
  return schedule
    .filter((item) => Array.isArray(item.days) && item.days.length && item.startTime && item.endTime)
    .map((item) => `${item.days.map((d) => labelMap[d] || d).join(", ")} · ${item.startTime}-${item.endTime}`)
    .join(" | ") || "Chưa có lịch";
}

export default function ClassDashboard({
  classInfo,
  students: initialStudents = [],
  teachers = [],
  allStudents = [],
  programs = [],
  user,
}) {
  const [tab, setTab] = useState("info");
  const [students, setStudents] = useState(initialStudents);

  useEffect(() => {
    setTab("info");
    setStudents(initialStudents);
  }, [classInfo?.id, initialStudents]);

  const program = useMemo(
    () => programs.find((p) => p.id === classInfo.programId) || {},
    [programs, classInfo?.programId]
  );

  const roles = Array.isArray(user?.roles) ? user.roles : user?.role ? [user.role] : [];
  const isAdmin = roles.includes("admin") || roles.includes("editor");
  const isMainTeacher = !!teachers?.some(
    (t) => t.id === user?.uid && (t.assignment?.role || t.role) === "main"
  );
  const canEdit = isAdmin || isMainTeacher;

  const teachersCompat = useMemo(() => {
    return (teachers || []).map((t) => {
      const name = t.display?.name || "";
      const [firstName, ...rest] = name.split(" ");
      const lastName = rest.join(" ");
      return {
        ...t,
        name,
        firstName,
        lastName,
        email: t.display?.email || "",
        avatarUrl: t.display?.avatarUrl || "",
        role: t.assignment?.role || t.role || "assistant",
      };
    });
  }, [teachers]);

  const studentsCompat = useMemo(() => {
    return (students || []).map((s) => {
      const disp = s.display || {};
      const name = disp.name || "";
      const [firstName, ...rest] = name.split(" ");
      const lastName = rest.join(" ");
      return {
        ...s,
        status: s.enrollment?.status || "active",
        enrolledAt: s.enrollment?.enrolledAt || null,
        name,
        firstName,
        lastName,
        email: disp.email || "",
        avatarUrl: disp.avatarUrl || "",
      };
    });
  }, [students]);

  const handleAddStudent = async (userObj) => {
    await addStudentToClass(classInfo.id, userObj);
    const display = pickDisplayFromUser(userObj);
    await logClassActivity({
      classId: classInfo.id,
      type: "student_enrolled",
      severity: "normal",
      targetId: userObj.id,
      targetTitle: display.name || userObj.email || userObj.id,
      message: `${display.name || userObj.email || "Học viên"} được thêm vào lớp.`,
    });
    setStudents((prev) => [
      ...prev,
      {
        id: userObj.id,
        display,
        enrollment: { status: "active", enrolledAt: new Date() },
      },
    ]);
  };

  const handleRemoveStudent = async (studentId) => {
    const student = students.find((s) => s.id === studentId);
    const name = student?.display?.name || student?.name || student?.email || studentId;
    await removeStudentFromClass(classInfo.id, studentId);
    await logClassActivity({
      classId: classInfo.id,
      type: "student_removed",
      severity: "warning",
      targetId: studentId,
      targetTitle: name,
      message: `${name} đã được xóa khỏi lớp.`,
    });
    setStudents((prev) => prev.filter((s) => s.id !== studentId));
  };

  const handleUpdateStatus = async (studentId, status, note) => {
    const student = students.find((s) => s.id === studentId);
    const name = student?.display?.name || student?.name || student?.email || studentId;
    await updateStudentStatus(classInfo.id, studentId, status, note);
    await logClassActivity({
      classId: classInfo.id,
      type: "student_status_changed",
      severity: status === "active" ? "normal" : "warning",
      targetId: studentId,
      targetTitle: name,
      message: `Cập nhật trạng thái của ${name}: ${status}${note ? ` — ${note}` : ""}.`,
    });
    setStudents((prev) =>
      prev.map((s) =>
        s.id === studentId
          ? {
              ...s,
              enrollment: {
                ...(s.enrollment || {}),
                status,
                note: note || (s.enrollment && s.enrollment.note) || undefined,
              },
            }
          : s
      )
    );
  };

  const teacherNames = teachersCompat
    .map((t) => t.name || `${t.firstName || ""} ${t.lastName || ""}`.trim())
    .filter(Boolean);

  return (
    <div className="max-w-7xl mx-auto py-4 space-y-5">
      <section className="rounded-2xl border bg-white p-5 md:p-6 shadow-sm">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${
                  classInfo.status === "active"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-slate-100 text-slate-600"
                }`}
              >
                {classInfo.status === "active" ? "Đang hoạt động" : "Ngừng hoạt động"}
              </span>
              <span className="inline-flex items-center gap-1 rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-700">
                <FiBookOpen /> {displayText(program.name || program.level, "Chưa chọn chương trình")}
              </span>
            </div>

            <h1 className="mt-3 text-2xl md:text-3xl font-bold text-slate-900">
              {displayText(classInfo.name, "Lớp học")}
            </h1>

            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-600">
              <span className="inline-flex items-center gap-1">
                <FiCalendar /> {renderScheduleShort(classInfo.schedule)}
              </span>
              <span className="inline-flex items-center gap-1">
                <FiMapPin /> {classInfo.room || "Chưa có phòng"}
              </span>
              <span className="inline-flex items-center gap-1">
                <FiUsers /> {studentsCompat.length}{classInfo.maxStudents ? ` / ${classInfo.maxStudents}` : ""} học viên
              </span>
            </div>

            <div className="mt-2 text-sm text-slate-500">
              GV phụ trách: {teacherNames.length ? teacherNames.join(", ") : "Chưa có"}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 lg:w-80">
            <div className="rounded-xl border bg-slate-50 px-3 py-3 text-center">
              <div className="text-xl font-bold text-blue-700">{studentsCompat.length}</div>
              <div className="text-xs text-slate-500">Học viên</div>
            </div>
            <div className="rounded-xl border bg-slate-50 px-3 py-3 text-center">
              <div className="text-xl font-bold text-emerald-600">{studentsCompat.filter((s) => s.status === "active").length}</div>
              <div className="text-xs text-slate-500">Đang học</div>
            </div>
            <div className="rounded-xl border bg-slate-50 px-3 py-3 text-center">
              <div className="text-xl font-bold text-amber-600">{teachersCompat.length}</div>
              <div className="text-xs text-slate-500">Giáo viên</div>
            </div>
          </div>
        </div>
      </section>

      <div className="rounded-2xl border bg-white p-2 flex flex-wrap gap-2 shadow-sm">
        {tabList.map((item) => {
          const Icon = item.icon || FiFileText;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition ${
                tab === item.key
                  ? "bg-blue-600 text-white shadow-sm"
                  : "text-slate-700 hover:bg-slate-100"
              }`}
            >
              <Icon />
              {item.label}
            </button>
          );
        })}
      </div>

      <section className="rounded-2xl border bg-white p-4 md:p-6 shadow-sm min-h-[350px]">
        {tab === "info" ? (
          <TabInfo
            classInfo={classInfo}
            students={studentsCompat}
            teachers={teachersCompat}
            programs={programs}
            isAdmin={isAdmin}
          />
        ) : tab === "students" ? (
          <TabStudents
            students={studentsCompat}
            classInfo={classInfo}
            allStudents={allStudents}
            canEdit={canEdit}
            onAddStudent={handleAddStudent}
            onRemoveStudent={handleRemoveStudent}
            onUpdateStatus={handleUpdateStatus}
          />
        ) : tab === "attendance" ? (
          <TabAttendance classInfo={classInfo} students={studentsCompat} />
        ) : tab === "assignments" ? (
          <TabAssignments classInfo={classInfo} students={studentsCompat} />
        ) : tab === "reports" ? (
          <TabReports classInfo={classInfo} students={studentsCompat} />
        ) : tab === "notifications" ? (
          <TabNotifications classInfo={classInfo} students={studentsCompat} />
        ) : tab === "activities" ? (
          <TabActivities classInfo={classInfo} students={studentsCompat} />
        ) : null}
      </section>
    </div>
  );
}
